/**
 * FM-S-multi-4-3 (rev. access-go-live 2026-07-08) — реакция на switch активной entity / scope.
 *
 * Раньше здесь был `queryClient.invalidateQueries()` — он РЕФЕТЧИЛ все mounted-запросы
 * с per-resource ID прежней компании (states по проектам и т.п.) уже под новым
 * X-Active-Entity → гарантированный шторм 404-тостов «объект не найден»
 * (инцидент 2026-07-08: «руководитель проектов» переключает компанию на /work-tasks).
 *
 * Теперь модуль только детектит смену scope и дёргает callback; `providers.tsx`
 * на callback ПЕРЕСОЗДАЁТ QueryClient. Пустой кэш ⇒ производные запросы
 * (states по projectId и т.п.) физически не могут выстрелить по ID чужой компании —
 * они появляются только после свежей загрузки родительских списков.
 *
 * Подписка устанавливается один раз в `providers.tsx` через `installActiveEntitySync`.
 * Debounce 100ms коалесцирует последовательные записи store (entity + scope).
 */
import { SCOPE_CHANGED_EVENT, useActiveEntityStore } from '~/store/active-entity';

let prevKey: string | null = null;

function scopeKey(): string {
  const s = useActiveEntityStore.getState();
  if (!s.isInitialized) return '';
  if (s.activeScope === 'all') return 'all';
  if (typeof s.activeScope === 'object') return `holding:${s.activeScope.holdingId}`;
  return `entity:${s.activeEntityId ?? ''}`;
}

let debounceTimer: ReturnType<typeof setTimeout> | null = null;

export function installActiveEntitySync(onScopeChange: () => void): () => void {
  // initialize key
  prevKey = scopeKey();

  const handleChange = (): void => {
    const newKey = scopeKey();
    if (newKey === prevKey || newKey === '') return;
    // Boot-hydrate ('' → первый key) — инициализация, НЕ переключение: без этого
    // прямой заход на detail-URL уводился на список (strip), а кэш ремаунтился
    // впустую сразу после загрузки (hotfix 2026-07-09, findings backlog).
    const wasBoot = prevKey === '';
    prevKey = newKey;
    if (wasBoot) return;

    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      onScopeChange();
    }, 100);
  };

  // Два канала (hotfix 2026-07-09): zustand-подписка (boot-hydrate) + window-событие
  // из самого store (клики в EntitySwitcher/CTA — на проде подписка на них молчала).
  // Оба сходятся в один guard по scopeKey + debounce — дубль безопасен.
  const unsubscribe = useActiveEntityStore.subscribe(handleChange);
  window.addEventListener(SCOPE_CHANGED_EVENT, handleChange);

  return () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    window.removeEventListener(SCOPE_CHANGED_EVENT, handleChange);
    unsubscribe();
  };
}

/** UUID где угодно в строке (query вида `?peek=<taskId>`). */
const UUID_RE = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/i;
/** Целый path-сегмент = UUID (детальные маршруты `/work-tasks/$projectId`, `/runs/$runId`…). */
const UUID_SEGMENT_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * Куда уводить пользователя при смене компании, если текущий URL держит
 * entity-scoped UUID (детальная страница / peek-drawer объекта прежней компании —
 * в новой компании такой ID даст легитимный 404):
 * - `/work-tasks/<uuid>[/gantt]` → `/work-tasks` (префикс до первого UUID-сегмента);
 * - UUID только в query (`?peek=<uuid>`) → тот же pathname без query;
 * - ничего stale-скоупного нет → `null` (остаёмся на месте).
 */
export function findStaleEntityPath(pathname: string, searchStr: string): string | null {
  const segments = pathname.split('/');
  const uuidIdx = segments.findIndex((seg) => UUID_SEGMENT_RE.test(seg));
  if (uuidIdx !== -1) return segments.slice(0, uuidIdx).join('/') || '/';
  if (UUID_RE.test(searchStr)) return pathname;
  return null;
}
